// ─── PEDIDOS DE CLIENTES ─────────────────────────────────────────────────────
// Ligado a Clientes, Inventario y Órdenes de compra:
//   - Stock: aparta inventario por tela y color (disponible = existencia −
//     apartados de otros pedidos abiertos). Se libera al marcar surtido/cancelado.
//   - Programación: genera una OC en borrador por proveedor (precio y referencia
//     de la última compra de cada tela). "Confirmar OC" abre Nueva OC ya llena.
import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "../auth";
import { EMPRESA, cargarLogoPdf } from "../empresa";
import { AccesoSL } from "./Clientes";
import { statusOC, fechaStatus } from "./statusOC";

const ESTADOS = {
  abierto: { label: "Abierto", cls: "bg-sky-100 text-sky-800" },
  programado: { label: "Programado", cls: "bg-violet-100 text-violet-800" },
  surtido: { label: "Surtido", cls: "bg-emerald-100 text-emerald-800" },
  cancelado: { label: "Cancelado", cls: "bg-slate-200 text-slate-600" },
};
const FILTROS = [
  { id: "activos", label: "Activos", estados: ["abierto", "programado"] },
  { id: "surtido", label: "Surtidos", estados: ["surtido"] },
  { id: "cancelado", label: "Cancelados", estados: ["cancelado"] },
  { id: "todos", label: "Todos", estados: null },
];

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
const num = (n, d = 2) =>
  n === null || n === undefined || n === "" || !isFinite(Number(n))
    ? "—"
    : Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: d });
const dinero = (n, moneda = "MXN") =>
  isFinite(Number(n)) ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${moneda}` : "—";
const hoyDMY = () => new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
const unidadInv = (u) => (/kg|kilo/i.test(u || "") ? "KG" : /m|mt|metro/i.test(u || "") ? "MT" : String(u || "").toUpperCase());
const importePedido = (p) => (p.renglones || []).reduce((s, r) => s + (Number(r.cantidad) || 0) * (Number(r.precio) || 0), 0);
const mensajeError = (e) => /"detail":"([^"]+)"/.exec(String(e?.message || ""))?.[1] || "No se pudo completar. Intenta de nuevo.";
// Etiqueta de color con el status de seguimiento de una OC (o ⛔ si se canceló).
function BadgeStatusOC({ info }) {
  if (!info) return null;
  if (info.cancelada)
    return <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200 whitespace-nowrap">⛔ Cancelada</span>;
  const st = statusOC(info.status);
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${st.cls}`} title={info.desde ? `${st.label} desde ${info.desde}` : st.label}>
      {st.label}
    </span>
  );
}

const consecutivo = (no) => +((/^\d+/.exec(String(no || "")) || ["0"])[0]);

const inputCls =
  "w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
const labelCls = "block text-[11px] font-semibold text-slate-500 mb-0.5";
const colorVacio = () => ({ id: Math.random().toString(36).slice(2, 10), color: "", cantidad: "" });
const bloqueVacio = () => ({ id: Math.random().toString(36).slice(2, 10), clave: "", descripcion: "", unidad: "", precio: "", proveedor: "", ref_proveedor: "", precio_compra: "", colores: [colorVacio()] });
// Junta los renglones de un pedido guardado por tela (misma tela, precio y proveedor).
const bloquesDesdeRenglones = (renglones) => {
  if (!renglones?.length) return [bloqueVacio()];
  const bloques = [];
  const porLlave = {};
  for (const r of renglones) {
    const k = [norm(r.clave || r.descripcion), norm(r.proveedor), r.precio ?? "", r.precio_compra ?? "", norm(r.ref_proveedor), norm(r.unidad)].join("|");
    if (!porLlave[k]) {
      porLlave[k] = {
        ...bloqueVacio(),
        clave: r.clave || "",
        descripcion: r.descripcion || "",
        unidad: r.unidad || "",
        precio: r.precio ?? "",
        proveedor: r.proveedor || "",
        ref_proveedor: r.ref_proveedor || "",
        precio_compra: r.precio_compra ?? "",
        colores: [],
      };
      bloques.push(porLlave[k]);
    }
    porLlave[k].colores.push({ id: r.id || colorVacio().id, color: r.color || "", cantidad: r.cantidad ?? "" });
  }
  return bloques;
};
// Aplana a renglones (lo que guarda el servidor); los colores sin cantidad no se mandan.
const renglonesDesdeBloques = (bloques) =>
  bloques.flatMap((x) =>
    x.colores
      .filter((c) => Number(c.cantidad) > 0)
      .map((c) => ({
        id: c.id,
        clave: x.clave,
        descripcion: x.descripcion,
        color: c.color,
        cantidad: c.cantidad,
        unidad: x.unidad,
        precio: x.precio,
        proveedor: x.proveedor,
        ref_proveedor: x.ref_proveedor,
        precio_compra: x.precio_compra,
      }))
  );

// ─── PDF del pedido (confirmación para el cliente) ───────────────────────────
async function pdfPedido(p) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(0, 0, 0);
  doc.rect(0, 0, W, 90, "F");
  try {
    const b64 = await cargarLogoPdf();
    doc.addImage(b64, "JPEG", W / 2 - 90, 10, 180, 66, undefined, "FAST");
  } catch (e) {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.text(EMPRESA.razonSocial, W / 2, 50, { align: "center" });
  }
  doc.setTextColor(15, 118, 110);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(`PEDIDO ${p.folio}`, 40, 122);
  autoTable(doc, {
    startY: 135,
    body: [
      ["Cliente", p.cliente?.nombre || "—", "Fecha", p.fecha || "—"],
      ["Tipo", p.tipo === "stock" ? "Stock (entrega inmediata)" : "Programación", "Entrega", p.fecha_entrega || "Por confirmar"],
      ["Vendedor", p.vendedor || "—", "Condiciones", p.condiciones || "—"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 5 },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 70, textColor: [15, 118, 110] }, 2: { fontStyle: "bold", cellWidth: 70, textColor: [15, 118, 110] } },
    margin: { left: 40, right: 40 },
  });
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 14,
    head: [["Tela", "Color", "Cantidad", "Unidad", "Precio", "Importe"]],
    body: (p.renglones || []).map((r) => [
      [r.descripcion, r.clave].filter(Boolean).join(" · "),
      r.color || "—",
      num(r.cantidad),
      r.unidad || "",
      r.precio != null ? dinero(r.precio, p.moneda) : "—",
      r.precio != null ? dinero(r.cantidad * r.precio, p.moneda) : "—",
    ]),
    foot: [["", "", "", "", "Total", dinero(importePedido(p), p.moneda)]],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [15, 118, 110] },
    footStyles: { fillColor: [240, 253, 250], textColor: [15, 23, 42], fontStyle: "bold" },
    columnStyles: { 2: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
    margin: { left: 40, right: 40 },
  });
  if (p.notas) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(doc.splitTextToSize(`Notas: ${p.notas}`, W - 80), 40, doc.lastAutoTable.finalY + 20);
  }
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text(`${EMPRESA.razonSocial} · Generado ${hoyDMY()} · Precios ${p.moneda || "MXN"} más IVA salvo que se indique otra cosa.`, W / 2, doc.internal.pageSize.getHeight() - 20, { align: "center" });
  doc.save(`Pedido ${p.folio} ${(p.cliente?.nombre || "").replace(/[<>:"/\\|?*]/g, "")}.pdf`);
}

// ─── Formulario de pedido (nuevo o editar) ───────────────────────────────────
function FormPedido({ pedido, clientes, clientesSL, slToken, onPedirSL, inventario, apartados, catalogo, onGuardado, onCerrar }) {
  const [tipo, setTipo] = useState(pedido?.tipo || "stock");
  // Cliente de factura o SL (remisión). SL solo con la contraseña SL.
  const [tipoCliente, setTipoCliente] = useState(pedido?.cliente?.tipo === "sl" ? "sl" : "factura");
  // Se pidió SL sin acceso: al llegar el acceso se selecciona solo.
  const [quiereSL, setQuiereSL] = useState(false);
  useEffect(() => {
    if (quiereSL && slToken) {
      setTipoCliente("sl");
      setClienteTxt("");
      setQuiereSL(false);
    }
  }, [quiereSL, slToken]);
  const [clienteTxt, setClienteTxt] = useState(pedido?.cliente?.nombre || "");
  const [vendedor, setVendedor] = useState(pedido?.vendedor || "");
  const [fecha, setFecha] = useState(pedido?.fecha || hoyDMY());
  const [fechaEntrega, setFechaEntrega] = useState(pedido?.fecha_entrega || "");
  const [moneda, setMoneda] = useState(pedido?.moneda || "MXN");
  const [condiciones, setCondiciones] = useState(pedido?.condiciones || "");
  const [notas, setNotas] = useState(pedido?.notas || "");
  // Captura por tela: el artículo, precio y proveedor se ponen una vez y cada
  // color solo lleva su cantidad. Al guardar se aplana a renglones (tela + color).
  const [bloques, setBloques] = useState(() => {
    const bs = bloquesDesdeRenglones(pedido?.renglones);
    if (pedido?.tipo !== "stock") return bs;
    // Al editar un pedido de stock se muestran todos los colores de la tela, no solo los pedidos.
    return bs.map((x) => {
      const it = inventario.find((i) => norm(i.id) === norm(x.clave));
      const cs = (it?.colores || []).map((c) => c.color);
      if (!cs.length) return x;
      const extra = cs.filter((c) => !x.colores.some((k) => norm(k.color) === norm(c))).map((c) => ({ ...colorVacio(), color: c }));
      return { ...x, colores: [...x.colores, ...extra].sort((a, b) => cs.findIndex((c) => norm(c) === norm(a.color)) - cs.findIndex((c) => norm(c) === norm(b.color))) };
    });
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  // Lista activa en el mismo formato ({id, nombreComercial, vendedor, ...}).
  const listaClientes = useMemo(
    () =>
      tipoCliente === "sl"
        ? (clientesSL || []).map((c) => ({ ...c, id: c.id_estrategia, nombreComercial: c.nombre, id_estrategia: c.id_estrategia }))
        : clientes,
    [tipoCliente, clientes, clientesSL]
  );
  const cliente = useMemo(() => listaClientes.find((c) => norm(c.nombreComercial) === norm(clienteTxt)), [listaClientes, clienteTxt]);
  const elegirCliente = (txt) => {
    setClienteTxt(txt);
    const c = listaClientes.find((x) => norm(x.nombreComercial) === norm(txt));
    if (c) {
      if (!vendedor) setVendedor(c.vendedor || "");
      if (!condiciones && c.condicionesPago) setCondiciones(c.condicionesPago === "contado" ? "Contado" : `${c.condicionesPago} días`);
    }
  };

  const invPorClave = useMemo(() => Object.fromEntries(inventario.map((it) => [norm(it.id), it])), [inventario]);
  // Lo apartado por este mismo pedido no cuenta contra su disponible.
  const propio = useMemo(() => {
    const m = {};
    if (pedido?.tipo === "stock" && pedido?.estado === "abierto")
      for (const r of pedido.renglones || []) m[`${norm(r.clave)}|${norm(r.color)}`] = (m[`${norm(r.clave)}|${norm(r.color)}`] || 0) + Number(r.cantidad || 0);
    return m;
  }, [pedido]);
  const disponible = (clave, color) => {
    const it = invPorClave[norm(clave)];
    if (!it) return 0;
    const cs = it.colores || [];
    const ex = cs.length ? cs.filter((c) => norm(c.color) === norm(color)).reduce((s, c) => s + Number(c.existencia || 0), 0) : Number(it.existencia || 0);
    const k = `${norm(clave)}|${norm(color)}`;
    // Llaves igual que el servidor: mayúsculas y espacios normalizados (sin quitar acentos).
    const sv = (x) => String(x || "").toUpperCase().replace(/\s+/g, " ").trim();
    const ap = (apartados[sv(clave)]?.[sv(color)] || 0) - (propio[k] || 0);
    return ex - ap;
  };

  const setB = (b, campo, valor) => setBloques((bs) => bs.map((x, k) => (k === b ? { ...x, [campo]: valor } : x)));
  const setColor = (b, j, campo, valor) =>
    setBloques((bs) => bs.map((x, k) => (k === b ? { ...x, colores: x.colores.map((c, m) => (m === j ? { ...c, [campo]: valor } : c)) } : x)));
  const agregarColor = (b) => setBloques((bs) => bs.map((x, k) => (k === b ? { ...x, colores: [...x.colores, colorVacio()] } : x)));
  const quitarColor = (b, j) => setBloques((bs) => bs.map((x, k) => (k === b ? { ...x, colores: x.colores.filter((_, m) => m !== j) } : x)));
  // En stock, al elegir la tela aparecen todos sus colores y solo se captura la cantidad.
  const elegirTelaStock = (b, txt) => {
    const it = inventario.find((x) => `${x.id} — ${x.descripcion}` === txt || norm(x.id) === norm(txt));
    setBloques((bs) =>
      bs.map((x, k) => {
        if (k !== b) return x;
        if (!it) return { ...x, clave: "", descripcion: txt, colores: [colorVacio()] };
        const previos = Object.fromEntries(x.colores.filter((c) => c.cantidad !== "").map((c) => [norm(c.color), c.cantidad]));
        const cs = (it.colores || []).map((c) => c.color);
        const colores = cs.length ? cs.map((c) => ({ ...colorVacio(), color: c, cantidad: norm(x.clave) === norm(it.id) ? previos[norm(c)] ?? "" : "" })) : [colorVacio()];
        return { ...x, clave: it.id, descripcion: it.descripcion, unidad: unidadInv(it.unidad), colores };
      })
    );
  };
  const elegirTelaProg = (b, txt) => {
    const c = catalogo.find((x) => x.etiqueta === txt);
    setBloques((bs) =>
      bs.map((x, k) =>
        k === b
          ? c
            ? { ...x, descripcion: c.tela, clave: c.clave || "", ref_proveedor: c.ref || "", proveedor: c.proveedor || x.proveedor, precio_compra: c.precio ?? x.precio_compra, unidad: c.unidad || x.unidad }
            : { ...x, descripcion: txt }
          : x
      )
    );
  };

  const renglones = useMemo(() => renglonesDesdeBloques(bloques), [bloques]);
  const cantidadBloque = (x) => x.colores.reduce((s, c) => s + (Number(c.cantidad) || 0), 0);
  const total = renglones.reduce((s, r) => s + (Number(r.cantidad) || 0) * (Number(r.precio) || 0), 0);
  const proveedoresConocidos = useMemo(() => [...new Set(catalogo.map((c) => c.proveedor).filter(Boolean))].sort(), [catalogo]);

  const guardar = async () => {
    setError("");
    if (!clienteTxt.trim()) return setError("Elige el cliente.");
    // Validación de stock en pantalla (el servidor vuelve a validar).
    if (tipo === "stock") {
      const suma = {};
      for (const r of renglones) {
        if (!r.clave || !(Number(r.cantidad) > 0)) continue;
        const k = `${norm(r.clave)}|${norm(r.color)}`;
        suma[k] = (suma[k] || 0) + Number(r.cantidad);
        if (suma[k] > disponible(r.clave, r.color) + 1e-6)
          return setError(`${r.descripcion}${r.color ? ` ${r.color}` : ""}: pides ${num(suma[k])} y hay ${num(Math.max(0, disponible(r.clave, r.color)))} disponibles.`);
      }
    }
    const payload = {
      tipo,
      cliente: { id: cliente?.id || "", nombre: cliente?.nombreComercial || clienteTxt.trim(), id_estrategia: cliente?.id_estrategia || "", tipo: tipoCliente },
      vendedor,
      fecha,
      fecha_entrega: fechaEntrega,
      moneda,
      condiciones,
      notas,
      renglones,
    };
    setGuardando(true);
    try {
      const h = slToken ? { "X-SL-Token": slToken } : {};
      const res = pedido?.id ? await api.post(`/pedidos/${pedido.id}`, payload, h) : await api.post("/pedidos", payload, h);
      onGuardado(res);
    } catch (e) {
      setError(mensajeError(e));
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-3 overflow-y-auto" data-testid="pedido-form">
      <div className="bg-white rounded-2xl w-full max-w-6xl p-5 space-y-4 my-4">
        <div className="flex items-start justify-between">
          <p className="text-sm font-bold text-slate-800">{pedido ? `Editar pedido ${pedido.folio}` : "Nuevo pedido"}</p>
          <button onClick={onCerrar} disabled={guardando} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2" title="Cerrar">
            ×
          </button>
        </div>

        <div className="flex gap-2" data-testid="pedido-tipo">
          {[
            ["stock", "📦 Stock", "Se aparta del inventario"],
            ["programacion", "🗓️ Programación", "Genera una orden de compra en borrador"],
          ].map(([id, t, d]) => (
            <button
              key={id}
              type="button"
              disabled={!!pedido}
              onClick={() => {
                setTipo(id);
                setBloques([bloqueVacio()]);
              }}
              data-testid={`pedido-tipo-${id}`}
              className={`flex-1 rounded-xl border-2 px-3 py-2 text-left ${tipo === id ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300"} ${pedido ? "cursor-not-allowed" : ""}`}
            >
              <p className="text-sm font-semibold text-slate-800">{t}</p>
              <p className="text-[11px] text-slate-500">{d}</p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="col-span-2">
            <div className="flex items-center justify-between">
              <label className={labelCls}>Cliente *</label>
              <div className="flex gap-1 mb-0.5" data-testid="pedido-tipo-cliente">
                {[
                  ["factura", "Factura"],
                  ["sl", "🔒 SL · remisión"],
                ].map(([id, t]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      if (id === "sl" && !slToken) {
                        setQuiereSL(true);
                        return onPedirSL();
                      }
                      setTipoCliente(id);
                      setClienteTxt("");
                    }}
                    data-testid={`pedido-cliente-${id}`}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded ${tipoCliente === id ? (id === "sl" ? "bg-amber-500 text-white" : "bg-teal-600 text-white") : "bg-slate-100 text-slate-600"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <input list="pedido-clientes" value={clienteTxt} onChange={(e) => elegirCliente(e.target.value)} className={inputCls} placeholder="Escribe para buscar…" data-testid="pedido-cliente" />
            <datalist id="pedido-clientes">
              {listaClientes.map((c) => (
                <option key={c.id} value={c.nombreComercial} />
              ))}
            </datalist>
            {clienteTxt && !cliente && (
              <p className="text-[10px] text-amber-700 mt-0.5">No está en la lista de clientes {tipoCliente === "sl" ? "SL" : "de factura"}; se guardará solo con el nombre.</p>
            )}
            {cliente && Number(cliente.saldo_vencido) > 0 && (
              <p className="text-[10px] text-rose-600 mt-0.5">⚠️ Tiene saldo vencido de {dinero(cliente.saldo_vencido)} ({cliente.dias_atraso_max} días de atraso).</p>
            )}
          </div>
          <div>
            <label className={labelCls}>Vendedor</label>
            <input value={vendedor} onChange={(e) => setVendedor(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Fecha</label>
            <input value={fecha} onChange={(e) => setFecha(e.target.value)} className={inputCls} placeholder="dd/mm/aaaa" />
          </div>
          <div>
            <label className={labelCls}>Fecha de entrega</label>
            <input value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)} className={inputCls} placeholder="dd/mm/aaaa" />
          </div>
          <div>
            <label className={labelCls}>Moneda</label>
            <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className={inputCls}>
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Condiciones de pago</label>
            <input value={condiciones} onChange={(e) => setCondiciones(e.target.value)} className={inputCls} placeholder="Contado, 30 días…" />
          </div>
          <div className="col-span-2 md:col-span-4">
            <label className={labelCls}>Notas</label>
            <input value={notas} onChange={(e) => setNotas(e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="space-y-3">
          {bloques.map((x, b) => {
            const it = invPorClave[norm(x.clave)];
            const conDesglose = tipo === "stock" && it && (it.colores || []).length > 0;
            const cant = cantidadBloque(x);
            return (
              <div key={x.id} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`pedido-bloque-${b}`}>
                <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end">
                  <div className="col-span-2 md:col-span-5">
                    <label className={labelCls}>Tela {bloques.length > 1 ? b + 1 : ""}</label>
                    {tipo === "stock" ? (
                      <>
                        <input
                          list="pedido-inventario"
                          value={x.clave ? `${x.clave} — ${x.descripcion}` : x.descripcion}
                          onChange={(e) => elegirTelaStock(b, e.target.value)}
                          className={inputCls}
                          placeholder="Busca clave o tela del inventario"
                          data-testid={`pedido-tela-${b}`}
                        />
                        {!x.clave && x.descripcion && <p className="text-[10px] text-amber-700">Elige una tela de la lista del inventario.</p>}
                      </>
                    ) : (
                      <input list="pedido-catalogo" value={x.descripcion} onChange={(e) => elegirTelaProg(b, e.target.value)} className={inputCls} placeholder="Tela (de compras anteriores o nueva)" data-testid={`pedido-tela-${b}`} />
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Unidad</label>
                    {tipo === "stock" ? (
                      <p className="py-1.5 text-sm">{x.unidad || "—"}</p>
                    ) : (
                      <select value={x.unidad} onChange={(e) => setB(b, "unidad", e.target.value)} className={inputCls}>
                        <option value="">—</option>
                        <option value="MT">MT</option>
                        <option value="KG">KG</option>
                      </select>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Precio venta</label>
                    <input inputMode="decimal" value={x.precio} onChange={(e) => setB(b, "precio", e.target.value)} className={`${inputCls} text-right`} placeholder="0.00" data-testid={`pedido-precio-${b}`} />
                  </div>
                  {tipo === "programacion" ? (
                    <>
                      <div className="md:col-span-2">
                        <label className={labelCls}>Proveedor *</label>
                        <input list="pedido-proveedores" value={x.proveedor} onChange={(e) => setB(b, "proveedor", e.target.value.toUpperCase())} className={inputCls} data-testid={`pedido-proveedor-${b}`} />
                      </div>
                      <div className="md:col-span-1">
                        <label className={labelCls}>Costo USD</label>
                        <input inputMode="decimal" value={x.precio_compra} onChange={(e) => setB(b, "precio_compra", e.target.value)} className={`${inputCls} text-right`} placeholder="0.00" />
                      </div>
                    </>
                  ) : (
                    <div className="md:col-span-3" />
                  )}
                  <div className="flex justify-end">
                    {bloques.length > 1 && (
                      <button type="button" onClick={() => setBloques((bs) => bs.filter((_, k) => k !== b))} className="text-xs text-rose-500 hover:bg-rose-50 rounded px-2 py-1.5" title="Quitar tela">
                        ✕ Quitar
                      </button>
                    )}
                  </div>
                  {tipo === "programacion" && (
                    <div className="col-span-2 md:col-span-5">
                      <input value={x.ref_proveedor} onChange={(e) => setB(b, "ref_proveedor", e.target.value)} className={`${inputCls} text-[11px]`} placeholder="Ref. proveedor" />
                    </div>
                  )}
                </div>

                {tipo === "stock" && !x.clave ? null : conDesglose ? (
                  <div>
                    <p className="text-[11px] text-slate-500 mb-1">Cantidad por color ({x.unidad || "—"}) · entre paréntesis lo disponible</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                      {x.colores.map((c, j) => {
                        const disp = disponible(x.clave, c.color);
                        const excede = Number(c.cantidad) > disp + 1e-6;
                        const agotado = disp <= 0 && !c.cantidad;
                        return (
                          <label key={c.id} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 ${excede ? "border-rose-300 bg-rose-50" : Number(c.cantidad) > 0 ? "border-teal-300 bg-teal-50" : "border-slate-200"} ${agotado ? "opacity-40" : ""}`}>
                            <span className="text-xs flex-1 min-w-0 truncate" title={c.color} data-testid={`pedido-color-${b}-${j}`}>
                              {c.color} <span className={excede ? "text-rose-600 font-semibold" : "text-slate-400"}>({num(Math.max(0, disp))})</span>
                            </span>
                            <input
                              inputMode="decimal"
                              value={c.cantidad}
                              disabled={agotado}
                              onChange={(e) => setColor(b, j, "cantidad", e.target.value)}
                              className="w-16 border border-slate-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-teal-500"
                              data-testid={`pedido-cantidad-${b}-${j}`}
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : tipo === "stock" ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Sin desglose por color · disponible {num(Math.max(0, disponible(x.clave, "")))}</span>
                    <input
                      inputMode="decimal"
                      value={x.colores[0]?.cantidad || ""}
                      onChange={(e) => setColor(b, 0, "cantidad", e.target.value)}
                      className="w-24 border border-slate-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Cantidad"
                      data-testid={`pedido-cantidad-${b}-0`}
                    />
                  </div>
                ) : (
                  <div>
                    <p className="text-[11px] text-slate-500 mb-1">Colores · solo captura color y cantidad (Enter en la cantidad agrega otro color)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                      {x.colores.map((c, j) => (
                        <div key={c.id} className="flex items-center gap-1">
                          <input
                            value={c.color}
                            onChange={(e) => setColor(b, j, "color", e.target.value)}
                            className="flex-1 min-w-0 border border-slate-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="Color"
                            data-testid={`pedido-color-${b}-${j}`}
                            autoFocus={j > 0 && j === x.colores.length - 1 && !c.color}
                          />
                          <input
                            inputMode="decimal"
                            value={c.cantidad}
                            onChange={(e) => setColor(b, j, "cantidad", e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && j === x.colores.length - 1) {
                                e.preventDefault();
                                agregarColor(b);
                              }
                            }}
                            className="w-20 border border-slate-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="Cant."
                            data-testid={`pedido-cantidad-${b}-${j}`}
                          />
                          {x.colores.length > 1 && (
                            <button type="button" onClick={() => quitarColor(b, j)} className="text-rose-400 hover:text-rose-600 text-xs px-1" title="Quitar color">
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => agregarColor(b)} className="mt-1.5 text-[11px] font-semibold text-teal-700 hover:underline" data-testid={`pedido-agregar-color-${b}`}>
                      + Color
                    </button>
                  </div>
                )}

                {cant > 0 && (
                  <p className="text-[11px] text-right text-slate-600">
                    {num(cant)} {x.unidad || ""} · {Number(x.precio) ? <b>{dinero(cant * Number(x.precio), moneda)}</b> : "sin precio"}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <datalist id="pedido-inventario">
          {inventario.map((it) => (
            <option key={it.uid || it.id} value={`${it.id} — ${it.descripcion}`} />
          ))}
        </datalist>
        <datalist id="pedido-catalogo">
          {catalogo.map((c) => (
            <option key={c.etiqueta} value={c.etiqueta} />
          ))}
        </datalist>
        <datalist id="pedido-proveedores">
          {proveedoresConocidos.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button type="button" onClick={() => setBloques((bs) => [...bs, bloqueVacio()])} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50" data-testid="pedido-agregar">
            + Agregar tela
          </button>
          <p className="text-sm text-slate-700">
            Total: <b>{dinero(total, moneda)}</b>
          </p>
        </div>
        {tipo === "programacion" && (
          <p className="text-[11px] text-slate-500">
            Al guardar se crea una orden de compra en borrador por proveedor. La confirmas desde el pedido (ahí toma su número consecutivo).
          </p>
        )}
        {error && <p className="text-xs text-rose-600 font-medium" data-testid="pedido-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} disabled={guardando} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600">
            Cancelar
          </button>
          <button type="button" onClick={guardar} disabled={guardando} className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50" data-testid="pedido-guardar">
            {guardando ? "Guardando…" : pedido ? "Guardar cambios" : tipo === "stock" ? "Guardar y apartar" : "Guardar y crear OC en borrador"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detalle del pedido ──────────────────────────────────────────────────────
function DetallePedido({ pedido: p, onEditar, onEstado, onConfirmarOC, onCerrar, infoOC = () => null }) {
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const est = ESTADOS[p.estado] || ESTADOS.abierto;
  const cambiar = async (estado, pregunta) => {
    if (pregunta && !window.confirm(pregunta)) return;
    setTrabajando(true);
    setError("");
    try {
      await onEstado(p, estado);
    } catch (e) {
      setError(mensajeError(e));
    }
    setTrabajando(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-3 overflow-y-auto" onClick={onCerrar} data-testid="pedido-detalle">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl p-5 space-y-3 my-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Pedido {p.folio}</p>
            <p className="text-base font-bold text-slate-800">
              {p.cliente?.nombre}
              {p.cliente?.tipo === "sl" && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">SL · remisión</span>}
            </p>
            <p className="text-xs text-slate-500">
              {p.fecha} · {p.tipo === "stock" ? "📦 Stock" : "🗓️ Programación"}
              {p.vendedor ? ` · Vendedor: ${p.vendedor}` : ""}
              {p.fecha_entrega ? ` · Entrega: ${p.fecha_entrega}` : ""}
              {p.condiciones ? ` · ${p.condiciones}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${est.cls}`}>{est.label}</span>
            <button onClick={onCerrar} className="text-slate-400 hover:text-slate-600 text-2xl leading-none px-2" title="Cerrar">
              ×
            </button>
          </div>
        </div>

        <table className="w-full text-xs border border-slate-200 rounded-xl overflow-hidden">
          <thead>
            <tr className="bg-slate-50 text-slate-500">
              <th className="text-left px-2 py-1.5 font-semibold">Tela</th>
              <th className="text-left px-2 py-1.5 font-semibold">Color</th>
              <th className="text-right px-2 py-1.5 font-semibold">Cantidad</th>
              <th className="text-right px-2 py-1.5 font-semibold">Precio</th>
              <th className="text-right px-2 py-1.5 font-semibold">Importe</th>
            </tr>
          </thead>
          <tbody>
            {(p.renglones || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-2 py-1.5">
                  {r.descripcion} {r.clave && <span className="text-slate-400 font-mono">{r.clave}</span>}
                </td>
                <td className="px-2 py-1.5">{r.color || "—"}</td>
                <td className="px-2 py-1.5 text-right">
                  {num(r.cantidad)} {r.unidad}
                </td>
                <td className="px-2 py-1.5 text-right">{r.precio != null ? dinero(r.precio, p.moneda) : "—"}</td>
                <td className="px-2 py-1.5 text-right font-semibold">{r.precio != null ? dinero(r.cantidad * r.precio, p.moneda) : "—"}</td>
              </tr>
            ))}
            <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
              <td className="px-2 py-1.5" colSpan={4}>
                Total
              </td>
              <td className="px-2 py-1.5 text-right">{dinero(importePedido(p), p.moneda)}</td>
            </tr>
          </tbody>
        </table>
        {p.notas && <p className="text-xs text-slate-600">📝 {p.notas}</p>}

        {p.tipo === "programacion" && p.oc && (
          <div className="border border-violet-200 bg-violet-50/50 rounded-xl p-3 space-y-2" data-testid="pedido-oc">
            <p className="text-xs font-semibold text-violet-800">Órdenes de compra de este pedido</p>
            {(p.oc.por_proveedor || []).map((g) => (
              <div key={g.proveedor} className="flex items-center justify-between gap-2 flex-wrap bg-white rounded-lg border border-violet-100 px-3 py-2">
                <div className="text-xs">
                  <p className="font-semibold text-slate-800">{g.proveedor}</p>
                  <p className="text-slate-500">{g.articulos.map((a) => `${a.tela} (${a.colores.length} ${a.colores.length === 1 ? "color" : "colores"})`).join(" · ")}</p>
                </div>
                {g.noOrden ? (
                  <div className="text-right" data-testid={`pedido-oc-status-${g.noOrden}`}>
                    <p className="text-xs font-semibold text-emerald-700">✅ OC {g.noOrden}</p>
                    <div className="mt-0.5 flex items-center justify-end gap-1.5">
                      <BadgeStatusOC info={infoOC(g.noOrden)} />
                      {infoOC(g.noOrden)?.desde && !infoOC(g.noOrden)?.cancelada && (
                        <span className="text-[10px] text-slate-400">desde {infoOC(g.noOrden).desde}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={p.estado === "cancelado"}
                    onClick={() => onConfirmarOC(p, g)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40"
                    data-testid={`pedido-confirmar-oc-${g.proveedor}`}
                  >
                    Confirmar OC (borrador)
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {p.historial?.length > 0 && (
          <details className="text-[11px] text-slate-500">
            <summary className="cursor-pointer">Historial</summary>
            {p.historial.map((h, i) => (
              <p key={i}>
                {new Date(h.fecha).toLocaleString("es-MX")} — {h.evento}
              </p>
            ))}
          </details>
        )}
        {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}

        <div className="flex flex-wrap gap-2 justify-between pt-1">
          <div className="flex gap-2 flex-wrap">
            {(p.estado === "abierto" || p.estado === "programado") && (
              <button type="button" disabled={trabajando} onClick={() => cambiar("surtido", p.tipo === "stock" ? "¿Marcar como surtido? Se libera lo apartado del inventario." : "¿Marcar como surtido?")} className="text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700" data-testid="pedido-surtir">
                ✅ Marcar surtido
              </button>
            )}
            {(p.estado === "abierto" || p.estado === "programado") && (
              <button type="button" disabled={trabajando} onClick={() => cambiar("cancelado", "¿Cancelar el pedido?" + (p.tipo === "stock" ? " Se libera lo apartado." : " Las OC ya confirmadas no se borran."))} className="text-xs font-semibold px-3 py-2 rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="pedido-cancelar">
                Cancelar pedido
              </button>
            )}
            {(p.estado === "surtido" || p.estado === "cancelado") && (
              <button type="button" disabled={trabajando} onClick={() => cambiar("abierto", "¿Reabrir el pedido?" + (p.tipo === "stock" ? " Se vuelve a apartar del inventario." : ""))} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
                Reabrir
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {p.estado === "abierto" && (
              <button type="button" onClick={() => onEditar(p)} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="pedido-editar">
                ✏️ Editar
              </button>
            )}
            <button type="button" onClick={() => pdfPedido(p)} className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800" data-testid="pedido-pdf">
              ⬇ PDF para cliente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Lista de pedidos ────────────────────────────────────────────────────────
export default function Pedidos({ onConfirmarOC, abrirPedidoId, slTokenInicial }) {
  const [pedidos, setPedidos] = useState([]);
  // Acceso SL: el token vive solo en memoria (igual que en Clientes).
  const [slToken, setSlToken] = useState(slTokenInicial || null);
  const [pidiendoSL, setPidiendoSL] = useState(false);
  const [clientesSL, setClientesSL] = useState([]);
  const [slOcultos, setSlOcultos] = useState(0);
  const hSL = slToken ? { "X-SL-Token": slToken } : undefined;
  const [clientes, setClientes] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [apartados, setApartados] = useState({});
  const [articulosOC, setArticulosOC] = useState([]);
  const [ocsStatus, setOcsStatus] = useState({}); // status de seguimiento por No. de OC
  // En qué va cada OC: status (Pendiente → Entregada) o cancelada.
  const infoOC = (noOrden) => {
    if (!noOrden) return null;
    const n = norm(noOrden);
    const arts = articulosOC.filter((a) => norm(a.hoja || a.noOrden) === n || norm(a.noOrden) === n);
    const cancelada = arts.length > 0 && arts.every((a) => a.cancelado === true || norm(a.cliente) === "CANCELADO");
    const info = ocsStatus[noOrden] || Object.entries(ocsStatus).find(([k]) => norm(k) === n)?.[1];
    return { cancelada, status: info?.status || "pendiente", desde: fechaStatus(info?.actualizado) };
  };
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("activos");
  const [busqueda, setBusqueda] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [form, setForm] = useState(null); // {pedido?} abierto
  const [detalle, setDetalle] = useState(null);

  const recargarApartados = async () => {
    try {
      const a = await api.get("/pedidos/apartados");
      setApartados(a?.apartados || {});
    } catch (e) {
      // silencioso
    }
  };

  useEffect(() => {
    (async () => {
      const [p, c, inv, oc] = await Promise.all([
        api.get("/pedidos", hSL).catch(() => null),
        api.get("/clientes").catch(() => null),
        api.get("/inventario").catch(() => null),
        api.get("/oc-workbook").catch(() => null),
      ]);
      const lista = Array.isArray(p?.pedidos) ? p.pedidos : [];
      setPedidos(lista);
      setSlOcultos(p?.sl_ocultos || 0);
      setClientes(Array.isArray(c?.clientes) ? [...c.clientes].sort((a, b) => String(a.nombreComercial).localeCompare(String(b.nombreComercial), "es")) : []);
      setInventario(Array.isArray(inv?.items) ? inv.items : []);
      setArticulosOC(Array.isArray(oc?.articulos) ? oc.articulos : []);
      setOcsStatus(oc?.ocs_status || {});
      await recargarApartados();
      setLoading(false);
      if (abrirPedidoId) setDetalle(lista.find((x) => x.id === abrirPedidoId) || null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Catálogo para programación: la compra más reciente de cada tela (proveedor,
  // referencia, precio y unidad) + telas del inventario que nunca se han comprado aquí.
  const catalogo = useMemo(() => {
    const porTela = {};
    for (const a of articulosOC) {
      if (!a.tela) continue;
      const k = norm(`${a.tela}|${a.proveedor}`);
      const prev = porTela[k];
      if (!prev || consecutivo(a.noOrden) > consecutivo(prev.noOrden)) porTela[k] = a;
    }
    const lista = Object.values(porTela).map((a) => ({
      etiqueta: `${a.tela}${a.ref ? ` (${a.ref})` : ""} · ${a.proveedor}`,
      tela: a.tela,
      ref: a.ref || "",
      proveedor: a.proveedor || "",
      precio: a.precioUnitario || a.fobUSD || "",
      unidad: /kg/i.test(a.unidad || "") ? "KG" : "MT",
      noOrden: a.noOrden,
    }));
    const conocidas = new Set(lista.map((c) => norm(c.tela)));
    for (const it of inventario) {
      if (!conocidas.has(norm(it.descripcion))) lista.push({ etiqueta: `${it.descripcion} · inventario ${it.id}`, tela: it.descripcion, clave: it.id, ref: "", proveedor: "", precio: "", unidad: unidadInv(it.unidad) });
    }
    return lista.sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es"));
  }, [articulosOC, inventario]);

  const filtrados = useMemo(() => {
    const f = FILTROS.find((x) => x.id === filtro);
    const q = norm(busqueda);
    return pedidos
      .filter((p) => !f.estados || f.estados.includes(p.estado))
      .filter((p) => !tipoFiltro || p.tipo === tipoFiltro)
      .filter((p) => !q || [p.folio, p.cliente?.nombre, p.vendedor, ...(p.renglones || []).flatMap((r) => [r.descripcion, r.clave, r.color])].some((v) => norm(v).includes(q)));
  }, [pedidos, filtro, busqueda, tipoFiltro]);

  // Con la contraseña SL se recargan los pedidos (incluye los SL) y la lista de clientes SL.
  const activarSL = async (token) => {
    setSlToken(token);
    setPidiendoSL(false);
    const h = { "X-SL-Token": token };
    try {
      const [p, c] = await Promise.all([api.get("/pedidos", h), api.get("/sl/clientes", h)]);
      setPedidos(Array.isArray(p?.pedidos) ? p.pedidos : []);
      setSlOcultos(p?.sl_ocultos || 0);
      setClientesSL(Array.isArray(c?.clientes) ? [...c.clientes].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), "es")) : []);
    } catch (e) {
      setSlToken(null);
    }
  };
  useEffect(() => {
    if (slTokenInicial) activarSL(slTokenInicial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reemplazar = (nuevo) => setPedidos((ps) => (ps.some((p) => p.id === nuevo.id) ? ps.map((p) => (p.id === nuevo.id ? nuevo : p)) : [nuevo, ...ps]));
  const cambiarEstado = async (p, estado) => {
    const nuevo = await api.post(`/pedidos/${p.id}/estado`, { estado }, hSL || {});
    reemplazar(nuevo);
    setDetalle(nuevo);
    recargarApartados();
  };

  const conteo = (estados) => pedidos.filter((p) => estados.includes(p.estado)).length;
  const totalActivos = pedidos.filter((p) => ["abierto", "programado"].includes(p.estado)).reduce((s, p) => s + (p.moneda === "USD" ? 0 : importePedido(p)), 0);

  return (
    <div className="space-y-3 pb-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Pedidos de clientes</p>
          <p className="text-sm text-slate-600 mt-0.5">
            {conteo(["abierto"])} abiertos · {conteo(["programado"])} programados · Activos: <b>{dinero(totalActivos)}</b>
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {!slToken ? (
            <button type="button" onClick={() => setPidiendoSL(true)} className="text-xs font-semibold px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" data-testid="pedidos-sl">
              🔒 SL{slOcultos > 0 ? ` (${slOcultos} ${slOcultos === 1 ? "oculto" : "ocultos"})` : ""}
            </button>
          ) : (
            <span className="text-[11px] font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800">🔓 Viendo pedidos SL</span>
          )}
          <button type="button" onClick={() => setForm({})} className="text-xs font-semibold px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800" data-testid="pedidos-nuevo">
            + Nuevo pedido
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {FILTROS.map((f) => (
            <button key={f.id} type="button" onClick={() => setFiltro(f.id)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${filtro === f.id ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`pedidos-filtro-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs">
          <option value="">Stock y programación</option>
          <option value="stock">Solo stock</option>
          <option value="programacion">Solo programación</option>
        </select>
        <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="🔎 Folio, cliente, vendedor, tela, color…" className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-1.5 text-sm" data-testid="pedidos-buscar" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500">
              <th className="text-left px-3 py-2 font-semibold">Folio</th>
              <th className="text-left px-3 py-2 font-semibold">Fecha</th>
              <th className="text-left px-3 py-2 font-semibold">Cliente</th>
              <th className="text-left px-3 py-2 font-semibold">Tipo</th>
              <th className="text-left px-3 py-2 font-semibold">Telas</th>
              <th className="text-right px-3 py-2 font-semibold">Importe</th>
              <th className="text-left px-3 py-2 font-semibold">Entrega</th>
              <th className="text-left px-3 py-2 font-semibold">OC</th>
              <th className="text-left px-3 py-2 font-semibold">Estado</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((p) => {
              const est = ESTADOS[p.estado] || ESTADOS.abierto;
              const ocs = (p.oc?.por_proveedor || []).map((g) => g.noOrden).filter(Boolean);
              const pendientes = (p.oc?.por_proveedor || []).filter((g) => !g.noOrden).length;
              return (
                <tr key={p.id} onClick={() => setDetalle(p)} className="border-t border-slate-100 hover:bg-teal-50/40 cursor-pointer" data-testid={`pedido-row-${p.folio}`}>
                  <td className="px-3 py-2 font-mono font-semibold">{p.folio}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.fecha}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {p.cliente?.nombre}
                    {p.cliente?.tipo === "sl" && <span className="ml-1.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800">SL</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.tipo === "stock" ? "📦 Stock" : "🗓️ Programación"}</td>
                  <td className="px-3 py-2">{(p.renglones || []).map((r) => r.descripcion).filter((v, i, a) => a.indexOf(v) === i).join(", ")}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">{dinero(importePedido(p), p.moneda)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.fecha_entrega || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {ocs.map((n) => (
                      <span key={n} className="inline-flex items-center gap-1 mr-2" data-testid={`pedidos-oc-${n}`}>
                        {n} <BadgeStatusOC info={infoOC(n)} />
                      </span>
                    ))}
                    {pendientes > 0 && p.estado !== "cancelado" && <span className="text-violet-700 font-semibold"> {pendientes} por confirmar</span>}
                    {p.tipo === "stock" && "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${est.cls}`}>{est.label}</span>
                  </td>
                </tr>
              );
            })}
            {!loading && filtrados.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-slate-400 py-8">
                  {pedidos.length === 0 ? "Todavía no hay pedidos. Crea el primero con «+ Nuevo pedido»." : "Ningún pedido coincide con los filtros."}
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={9} className="text-center text-slate-400 py-8">
                  Cargando…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <FormPedido
          pedido={form.pedido}
          clientes={clientes}
          clientesSL={clientesSL}
          slToken={slToken}
          onPedirSL={() => setPidiendoSL(true)}
          inventario={inventario}
          apartados={apartados}
          catalogo={catalogo}
          onCerrar={() => setForm(null)}
          onGuardado={(p) => {
            reemplazar(p);
            setForm(null);
            setDetalle(p);
            recargarApartados();
          }}
        />
      )}
      {detalle && !form && (
        <DetallePedido
          infoOC={infoOC}
          pedido={detalle}
          onCerrar={() => setDetalle(null)}
          onEditar={(p) => setForm({ pedido: p })}
          onEstado={cambiarEstado}
          onConfirmarOC={(p, g) => onConfirmarOC?.(p, g, articulosOC, slToken)}
        />
      )}
      {pidiendoSL && <AccesoSL onAcceso={activarSL} onCancelar={() => setPidiendoSL(false)} />}
    </div>
  );
}
